import React, { useId } from "react";
import Icon from "../icons/Icon";
import Modal from "./Modal";

/**
 * Reusable confirmation modal component — a thin wrapper over the shared
 * <Modal> shell (PRD_ModalBehavior). Backdrop clicks never close it; Esc and
 * the shell's × do, unless `loading`.
 *
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Called when user cancels, presses Esc or clicks ×
 * @param {function} onConfirm - Called when user confirms the action
 * @param {string} title - Modal title (default: "Confirm Action")
 * @param {string} message - The confirmation message to display
 * @param {string} confirmText - Text for confirm button (default: "Confirm")
 * @param {string} cancelText - Text for cancel button (default: "Cancel")
 * @param {string} variant - "danger" for destructive actions, "warning" for caution (default: "danger")
 * @param {boolean} loading - Show loading state on confirm button; locks every dismiss path (C4)
 * @param {React.ReactNode} children - Optional content (e.g. a form) rendered below the message
 */
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger",
  loading = false,
  children,
}) => {
  const titleId = useId();

  const colors = {
    danger: {
      icon: "var(--ui-danger)",
      iconBg: "var(--ui-danger-tint)",
      button: "var(--ui-danger)",
      buttonHover: "var(--ui-danger-hover)",
    },
    warning: {
      icon: "var(--ui-warning)",
      iconBg: "var(--ui-warning-tint)",
      button: "var(--ui-warning)",
      buttonHover: "var(--ui-warning-hover)",
    },
  };

  const colorScheme = colors[variant] || colors.danger;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      busy={loading}
      labelledBy={titleId}
      style={{
        background: "white",
        borderRadius: "12px",
        padding: "1.5rem",
        maxWidth: "400px",
        width: "90%",
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      }}
    >
      {/* Icon */}
      <div
        style={{
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          background: colorScheme.iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 1rem",
        }}
      >
        {variant === "danger" ? (
          <Icon name="trash-2" size={24} />
        ) : (
          <Icon name="alert-triangle" size={24} />
        )}
      </div>

      {/* Title */}
      <h3
        id={titleId}
        style={{
          fontSize: "1.125rem",
          fontWeight: "600",
          color: "var(--ui-text)",
          textAlign: "center",
          margin: "0 0 0.5rem",
        }}
      >
        {title}
      </h3>

      {/* Message */}
      <p
        style={{
          fontSize: "0.875rem",
          color: "var(--ui-text-muted)",
          textAlign: "center",
          margin: "0 0 1.5rem",
          lineHeight: "1.5",
        }}
      >
        {message}
      </p>

      {/* Optional content (e.g. a form) — left-aligned, not inheriting the centered
          message style, and scrollable so a tall form fits the fixed-width card. */}
      {children && (
        <div
          style={{
            textAlign: "left",
            maxHeight: "40vh",
            overflowY: "auto",
            margin: "0 0 1.5rem",
          }}
        >
          {children}
        </div>
      )}

      {/* Buttons — type="button" so a ConfirmModal rendered inside a <form>
          never submits it. */}
      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          justifyContent: "center",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          style={{
            padding: "0.625rem 1.25rem",
            borderRadius: "8px",
            border: "1px solid var(--ui-border)",
            background: "white",
            color: "var(--ui-text-secondary)",
            fontSize: "0.875rem",
            fontWeight: "500",
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.5 : 1,
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.backgroundColor = "var(--ui-bg)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "white";
          }}
        >
          {cancelText}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={loading}
          style={{
            padding: "0.625rem 1.25rem",
            borderRadius: "8px",
            border: "none",
            background: colorScheme.button,
            color: "white",
            fontSize: "0.875rem",
            fontWeight: "500",
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.7 : 1,
            transition: "background-color 0.2s",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.backgroundColor = colorScheme.buttonHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colorScheme.button;
          }}
        >
          {loading && (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              style={{ animation: "spin 1s linear infinite" }}
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                strokeDasharray="32"
                strokeLinecap="round"
              />
            </svg>
          )}
          {confirmText}
        </button>
      </div>

      {/* Spinner keyframe (the card's own entrance is opacity-only, in app-modal.css) */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Modal>
  );
};

export default ConfirmModal;
